import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Link } from 'react-router-dom'
import { 
  Server, 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Bell,
  BarChart3,
  PieChart,
  RefreshCw
} from 'lucide-react'
import { api, serversApi, MCPServer } from '@/lib/api'

interface DashboardData {
  global_metrics: {
    total_servers: number
    active_servers: number
    total_requests: number
    total_errors: number
    total_users: number
    total_teams: number
    system_uptime: string
    transport_stats: Record<string, number>
    top_tools: Array<{
      tool_name: string
      usage_count: number
      server_id: string
    }>
  }
  recent_activity: Array<{
    timestamp: string
    server_id: string
    server_name: string
    action: string
    success: boolean
    error_message?: string
  }>
  summary: {
    total_servers: number
    active_servers: number
    total_requests: number
    total_errors: number
    error_rate: number
    uptime: string
  }
}

interface ServerSummary {
  server_id: string
  server_name: string
  status: string
  request_count: number
  error_count: number
  last_activity: string
  transport: string
  tools_count: number
}

interface AlertSummary {
  id: string
  type: string
  severity: string
  message: string
  server_id: string
  timestamp: string
  status: string
}

export function DashboardPage() {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [serverSummary, setServerSummary] = useState<ServerSummary[]>([])
  const [fallbackServers, setFallbackServers] = useState<MCPServer[]>([])
  const [alerts, setAlerts] = useState<AlertSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  useEffect(() => {
    loadDashboardData()
    
    // Set up real-time updates every 30 seconds
    const interval = setInterval(loadDashboardData, 30000)
    return () => clearInterval(interval)
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      
      // Load dashboard data using the API client (which automatically includes auth tokens)
      const [dashboardResponse, serversResponse, alertsResponse] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/analytics'),
        api.get('/alerts?status=active&limit=5').catch(() => ({ alerts: [] })) // Handle if alerts endpoint doesn't exist
      ])

      setDashboardData(dashboardResponse as any)
      const summary = (serversResponse as any).server_summary || []
      setServerSummary(summary)
      if (Array.isArray(summary) && summary.length === 0) {
        try {
          const list = await serversApi.list()
          setFallbackServers(Array.isArray(list?.servers) ? list.servers : [])
        } catch (_) {
          setFallbackServers([])
        }
      } else {
        setFallbackServers([])
      }
      setAlerts((alertsResponse as any).alerts || [])
      setLastRefresh(new Date())
      setError(null)
    } catch (err) {
      console.error('Failed to load dashboard data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const formatUptime = (uptimeStr: string | number) => {
    // Handle null/undefined
    if (uptimeStr == null) return '0s'
    
    // If it's a number, assume it's nanoseconds and convert to readable format
    if (typeof uptimeStr === 'number') {
      const seconds = Math.floor(uptimeStr / 1e9) // Convert nanoseconds to seconds
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const remainingSeconds = seconds % 60
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`
      } else if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`
      } else {
        return `${remainingSeconds}s`
      }
    }
    
    // If it's a string, parse it as before
    if (typeof uptimeStr === 'string') {
      if (uptimeStr.includes('h')) {
        const hours = parseInt(uptimeStr.match(/(\d+)h/)?.[1] || '0')
        const minutes = parseInt(uptimeStr.match(/(\d+)m/)?.[1] || '0')
        return `${hours}h ${minutes}m`
      }
      return uptimeStr
    }
    
    // Fallback
    return '0s'
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
      case 'healthy':
        return 'bg-green-500'
      case 'error':
      case 'unhealthy':
        return 'bg-red-500'
      case 'warning':
        return 'bg-yellow-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'destructive'
      case 'warning':
        return 'default'
      case 'info':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  const calculateErrorRate = (totalRequests: number, totalErrors: number) => {
    if (totalRequests === 0) return 0
    return ((totalErrors / totalRequests) * 100).toFixed(2)
  }

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading dashboard...</span>
      </div>
    )
  }

  if (error && !dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <span className="ml-2 text-red-600">Error: {error}</span>
        <Button onClick={loadDashboardData} className="ml-4">
          Retry
        </Button>
      </div>
    )
  }

  const metrics = dashboardData?.global_metrics || {
    total_servers: 0,
    active_servers: 0,
    total_requests: 0,
    total_errors: 0,
    total_users: 0,
    total_teams: 0,
    system_uptime: '0s',
    transport_stats: {},
    top_tools: []
  }

  // Keep for future use if needed; currently not used
  // const summary = dashboardData?.summary || {
  //   total_servers: 0,
  //   active_servers: 0,
  //   total_requests: 0,
  //   total_errors: 0,
  //   error_rate: 0,
  //   uptime: '0s'
  // }

  // Ensure arrays are never null with comprehensive null safety
  const safeTopTools = (metrics?.top_tools && Array.isArray(metrics.top_tools)) ? metrics.top_tools : []
  const safeRecentActivity = (dashboardData?.recent_activity && Array.isArray(dashboardData.recent_activity)) ? dashboardData.recent_activity : []
  const safeTransportStats = metrics?.transport_stats || {}
  const safeAlerts = Array.isArray(alerts) ? alerts : []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
          <p className="text-muted-foreground">
            Real-time insights into your MCP servers and infrastructure.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="outline" className="text-xs">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Badge>
          <Button 
            onClick={loadDashboardData} 
            size="sm" 
            variant="outline"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Servers</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.total_servers}</div>
            <p className="text-xs text-muted-foreground">
              <span className={`inline-block w-2 h-2 rounded-full mr-1 ${getStatusColor('running')}`}></span>
              {metrics.active_servers} running
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.total_requests.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.total_errors} errors ({calculateErrorRate(metrics.total_requests, metrics.total_errors)}%)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Uptime</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatUptime(metrics.system_uptime)}</div>
            <p className="text-xs text-muted-foreground">
              <CheckCircle className="inline w-3 h-3 text-green-500 mr-1" />
              System healthy
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Alerts</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{safeAlerts.length}</div>
            <p className="text-xs text-muted-foreground">
              {safeAlerts.filter(a => a?.severity === 'critical').length} critical
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="servers">Servers</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Transport Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <PieChart className="h-5 w-5 mr-2" />
                  Transport Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(safeTransportStats).map(([transport, count]) => (
                    <div key={transport} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{transport}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${(count / metrics.total_servers) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Tools */}
            {/* <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Most Used Tools
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {safeTopTools.slice(0, 5).map((tool) => (
                    <div key={`${tool.server_id}-${tool.tool_name}`} className="flex items-center justify-between">
                      <span className="text-sm truncate">{tool.tool_name}</span>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="text-xs">
                          {tool.usage_count}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card> */}
            <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>
                Latest server events and activities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {safeRecentActivity.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className={`w-2 h-2 rounded-full ${activity.success ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {activity.action} - {activity.server_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(activity.timestamp).toLocaleString()}
                        {activity.error_message && (
                          <span className="text-red-500 ml-2">({activity.error_message})</span>
                        )}
                      </p>
                    </div>
                  </div>
                )) || (
                  <p className="text-sm text-muted-foreground">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Recent Activity */}
          {/* <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>
                Latest server events and activities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {safeRecentActivity.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className={`w-2 h-2 rounded-full ${activity.success ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {activity.action} - {activity.server_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(activity.timestamp).toLocaleString()}
                        {activity.error_message && (
                          <span className="text-red-500 ml-2">({activity.error_message})</span>
                        )}
                      </p>
                    </div>
                  </div>
                )) || (
                  <p className="text-sm text-muted-foreground">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card> */}
        </TabsContent>

        <TabsContent value="servers" className="space-y-4">
          {serverSummary.length > 0 ? (
            <div className="grid gap-4">
              {serverSummary.map((server) => (
                <Card key={server.server_id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{server.server_name}</CardTitle>
                      <div className="flex items-center space-x-2">
                        <Badge variant={server.status === 'running' ? 'default' : 'destructive'}>
                          {server.status}
                        </Badge>
                        <Badge variant="outline">{server.transport}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Requests</p>
                        <p className="font-medium">{server.request_count.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Errors</p>
                        <p className="font-medium text-red-600">{server.error_count}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Tools</p>
                        <p className="font-medium">{server.tools_count}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Last Activity</p>
                        <p className="font-medium">{new Date(server.last_activity).toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : fallbackServers.length > 0 ? (
            <div className="grid gap-4">
              {fallbackServers.map((srv) => (
                <Card key={srv.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{srv.name}</CardTitle>
                      <div className="flex items-center space-x-2">
                        <Badge variant={srv.status === 'running' ? 'default' : 'destructive'}>
                          {srv.status}
                        </Badge>
                        <Badge variant="outline">{srv.transport}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm">
                      <div className="text-muted-foreground">Created {new Date(srv.created_at).toLocaleString()}</div>
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/servers/${srv.id}`}>Open</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Server className="h-5 w-5 mr-2" />
                  No server data yet
                </CardTitle>
                <CardDescription>
                  When servers receive traffic, summary metrics will appear here.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-2">
                  <Button asChild size="sm">
                    <Link to="/servers">Create or start a server</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <div className="space-y-4">
            {safeAlerts.length > 0 ? safeAlerts.map((alert) => (
              <Card key={alert.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center">
                      <AlertTriangle className="h-5 w-5 mr-2 text-red-500" />
                      {alert.type.replace(/_/g, ' ').toUpperCase()}
                    </CardTitle>
                    <Badge variant={getSeverityColor(alert.severity)}>
                      {alert.severity}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-2">{alert.message}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Server: {alert.server_id}</span>
                    <span>{new Date(alert.timestamp).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                    <h3 className="text-lg font-semibold">No Active Alerts</h3>
                    <p className="text-muted-foreground">All systems are operating normally.</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Top Erroring Servers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Top Erroring Servers
                </CardTitle>
                <CardDescription>
                  Servers with the highest error counts. Drill down to logs.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(serverSummary.length > 0 ? [...serverSummary].sort((a, b) => b.error_count - a.error_count).slice(0, 5) : []).map((server) => (
                    <div key={server.server_id} className="flex items-center justify-between p-3 border rounded-md">
                      <div>
                        <div className="font-medium">{server.server_name}</div>
                        <div className="text-xs text-muted-foreground">Last activity: {new Date(server.last_activity).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="destructive">{server.error_count} errors</Badge>
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/servers/${server.server_id}?tab=logs`}>View logs</Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                  {serverSummary.length === 0 && (
                    <p className="text-sm text-muted-foreground">No server metrics available yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="h-5 w-5 mr-2" />
                  Recent Activity
                </CardTitle>
                <CardDescription>
                  Latest server events. Click through for details.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {safeRecentActivity.slice(0, 6).map((ev, idx) => (
                    <div key={`${ev.server_id}-${idx}`} className="flex items-center justify-between p-3 border rounded-md">
                      <div>
                        <div className="text-sm font-medium">{ev.action}</div>
                        <div className="text-xs text-muted-foreground">{ev.server_name} • {new Date(ev.timestamp).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${ev.success ? 'bg-green-500' : 'bg-red-500'}`} />
                        {/* <Button asChild size="sm" variant="outline">
                          <Link to={`/servers/${ev.server_id}?tab=logs`}>Details</Link>
                        </Button> */}
                      </div>
                    </div>
                  ))}
                  {safeRecentActivity.length === 0 && (
                    <p className="text-sm text-muted-foreground">No recent activity.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          {/* <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Create New Server</CardTitle>
                <CardDescription>
                  Deploy a new MCP server from an OpenAPI specification
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/servers">Create Server</Link>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Usage Analytics</CardTitle>
                <CardDescription>
                  View detailed usage analytics and reports
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/analytics">View Analytics</Link>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>System Monitoring</CardTitle>
                <CardDescription>
                  Monitor server health and performance metrics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/monitoring">View Monitoring</Link>
                </Button>
              </CardContent>
            </Card>
          </div> */}
        </TabsContent>
      </Tabs>
    </div>
  )
} 